/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent, JSX } from "react";

import type { StarRatingProps } from "./StarRatingProps";
import "./StarRating.scss";

/**
 * Renders a row of stars representing a fractional rating.
 *
 * Supports filled, half-filled and empty stars.
 * A half star is shown whenever the fractional part of `rating` is >= 0.5.
 * The maximum number of stars is configurable via `maxStars` (defaults to 4).
 *
 * Returns `<></>` when `rating` is negative.
 *
 * @param props.rating     - The numeric rating value (e.g. `3.5`).
 * @param props.maxStars   - Total number of stars in the row. Defaults to `4`.
 * @param props.classStyle - Additional CSS class appended to the root `<div>`.
 */
const StarRating: FunctionComponent<StarRatingProps> = (
  props: StarRatingProps
): JSX.Element => {
  const TAG: string = "StarRating";
  const { rating, maxStars = 4, classStyle = "" } = props;

  if (
    rating === null ||
    rating === undefined ||
    rating < 0 ||
    maxStars < 0 ||
    !Number.isInteger(maxStars)
  )
    return <></>;

  const clamped = Math.min(rating, maxStars);
  const filled = Math.floor(clamped);
  const hasHalf = clamped % 1 >= 0.5;

  const baseClass = "star-rating" + (classStyle ? " " + classStyle : "");

  return (
    <div className={baseClass} data-tag={TAG}>
      {Array.from({ length: maxStars }).map((_, idx) => {
        let state: string;
        if (idx < filled) {
          state = "filled";
        } else if (idx === filled && hasHalf) {
          state = "half";
        } else {
          state = "empty";
        }

        const cls = `star-rating__star star-rating__star--${state}`;
        return <span key={idx} className={cls} />;
      })}
    </div>
  );
};

export default StarRating;
